import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Briefcase, Calendar } from 'lucide-react';
import RecruiterLayout from '@/components/RecruiterLayout';

const CandidateApplications = () => {
  const { candidateId } = useParams();
  const [applications, setApplications] = useState<any[]>([]);
  const [candidate, setCandidate] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (candidateId) {
      fetchData();
    }
  }, [candidateId]);

  const fetchData = async () => {
    try {
      // Fetch candidate info
      const { data: candidateData } = await supabase
        .from('candidates')
        .select('*')
        .eq('id', candidateId)
        .single();
      
      setCandidate(candidateData);

      // Fetch applications
      const { data: appsData } = await supabase
        .from('candidate_applications')
        .select(`
          *,
          jobs (
            id,
            title,
            location,
            job_type,
            status,
            clients (name)
          )
        `)
        .eq('candidate_id', candidateId)
        .order('applied_at', { ascending: false });

      setApplications(appsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RecruiterLayout>
    );
  }

  return (
    <RecruiterLayout>
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">
            {candidate?.first_name} {candidate?.last_name}'s Applications
          </h1>
          <p className="text-muted-foreground">
            {applications.length} job application{applications.length !== 1 ? 's' : ''}
          </p>
        </div>

        {applications.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No applications yet</h3>
            <p className="text-muted-foreground">
              This candidate hasn't applied to any jobs
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card key={app.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold mb-1">{app.jobs?.title}</h3>
                    {app.jobs?.clients && (
                      <p className="text-primary">{app.jobs.clients.name}</p>
                    )}
                  </div>
                  <Badge variant={
                    app.status === 'new' ? 'default' :
                    app.status === 'reviewing' ? 'secondary' :
                    app.status === 'interview' ? 'outline' :
                    'secondary'
                  }>
                    {app.status}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                  {app.jobs?.location && (
                    <div className="flex items-center gap-1">
                      <Briefcase className="h-4 w-4" />
                      {app.jobs.location}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Applied {new Date(app.applied_at).toLocaleDateString()}
                  </div>
                  {app.jobs?.job_type && (
                    <div>{app.jobs.job_type.replace('_', ' ')}</div>
                  )}
                </div>

                {app.notes && (
                  <div className="mt-4 p-3 bg-muted rounded-md">
                    <p className="text-sm text-muted-foreground">{app.notes}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </RecruiterLayout>
  );
};

export default CandidateApplications;
